import { Injectable, inject } from '@angular/core';
import { createEffect, ofType } from '@ngrx/effects';
import { LOCAL_ACTIONS } from '../../../util/local-actions.token';
import {
  addSubTask,
  setCurrentTask,
  toggleStart,
  unsetCurrentTask,
} from './task.actions';
import { TaskSharedActions } from '../../../root-store/meta/task-shared.actions';
import { select, Store } from '@ngrx/store';
import { filter, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import { selectTaskFeatureState } from './task.selectors';
import {
  selectConfigFeatureState,
  selectTasksConfig,
} from '../../config/store/global-config.reducer';
import { Task, TaskState } from '../task.model';
import { EMPTY, of } from 'rxjs';
import { WorkContextService } from '../../work-context/work-context.service';
import { selectTodayTaskIds } from '../../work-context/store/work-context.selectors';
import {
  moveProjectTaskToBacklogList,
  moveProjectTaskToBacklogListAuto,
} from '../../project/store/project.actions';
import { DateService } from '../../../core/date/date.service';
import { filterOutTodayTag } from '../../../root-store/meta/task-shared-meta-reducers/task-shared-helpers';
import { fastArrayCompare } from '../../../util/fast-array-compare';

@Injectable()
export class TaskInternalEffects {
  private _actions$ = inject(LOCAL_ACTIONS);
  private _store$ = inject(Store);
  private _workContextSession = inject(WorkContextService);
  private _dateService = inject(DateService);

  onAllSubTasksDone$ = createEffect(() =>
    this._actions$.pipe(
      ofType(TaskSharedActions.updateTask),
      withLatestFrom(
        this._store$.pipe(select(selectTasksConfig)),
        this._store$.pipe(select(selectTaskFeatureState)),
      ),
      filter(
        ([{ task }, tasksCfg, state]) =>
          !!tasksCfg &&
          tasksCfg.isAutoMarkParentAsDone &&
          !!task.changes.isDone &&
          !!state.entities[task.id as string]?.parentId,
      ),
      filter(([action, miscCfg, state]) => {
        const task = state.entities[action.task.id];
        if (!task || !task.parentId) {
          return false;
        }
        const parent = state.entities[task.parentId] as Task;
        const undoneSubTasks = parent.subTaskIds.filter(
          (id) => !(state.entities[id] as Task).isDone,
        );
        return undoneSubTasks.length === 0;
      }),
      map(([action, miscCfg, state]) =>
        TaskSharedActions.updateTask({
          task: {
            id: (state.entities[action.task.id] as Task).parentId as string,
            changes: { isDone: true },
          },
        }),
      ),
    ),
  );

  setDefaultEstimateIfNonGiven$ = createEffect(() =>
    this._actions$.pipe(
      ofType(TaskSharedActions.addTask, addSubTask),
      filter(({ task }) => !task.timeEstimate),
      withLatestFrom(this._store$.pipe(select(selectConfigFeatureState))),
      map(([action, cfg]) => ({
        timeEstimate:
          (action.task.parentId || (action.type === addSubTask.type && action.parentId)
            ? cfg.timeTracking.defaultEstimateSubTasks
            : cfg.timeTracking.defaultEstimate) || 0,
        task: action.task,
      })),
      filter(({ timeEstimate }) => timeEstimate > 0),
      map(({ task, timeEstimate }) =>
        TaskSharedActions.updateTask({
          task: {
            id: task.id,
            changes: {
              timeEstimate,
            },
          },
        }),
      ),
    ),
  );

  /**
   * #9651 graceful degradation: this client keeps a task's own tags across
   * convertToSubTask / convertToMainTask, but clients released before that
   * change (<= v18.20.1) replay these ops by wiping (to-sub) or overwriting
   * with the parent's (to-main) tagIds. Re-asserting the kept tags as a
   * follow-up updateTask op makes those clients converge to the same state.
   * Fires only on the originating client (LOCAL_ACTIONS), only when the
   * convert actually applied and old clients would end up with different
   * tags. Removable once pre-change clients are no longer a concern.
   */
  reassertOwnTagsAfterConvert$ = createEffect(() =>
    this._actions$.pipe(
      ofType(TaskSharedActions.convertToSubTask, TaskSharedActions.convertToMainTask),
      withLatestFrom(this._store$.pipe(select(selectTaskFeatureState))),
      mergeMap(([action, state]) => {
        const isConvertToSub = action.type === TaskSharedActions.convertToSubTask.type;
        const taskId = isConvertToSub ? action.taskId : action.task.id;
        const task = state.entities[taskId];
        if (!task) {
          return EMPTY;
        }
        // TODAY is virtual (rule 5) and must never be re-asserted into synced
        // tagIds — legacy-dirty data may still carry it on the to-sub path
        // (to-main already filters in the reducer).
        const ownTagIds = filterOutTodayTag(task.tagIds ?? []);
        if (!ownTagIds.length) {
          return EMPTY;
        }
        // NOTE: for a convert the reducer guard rejected (e.g. already nested
        // under the target), this still reads as applied and emits a redundant
        // but idempotent op — prior state isn't available here to tell apart.
        const isApplied = isConvertToSub
          ? task.parentId === action.targetParentId
          : !task.parentId;
        if (!isApplied) {
          return EMPTY;
        }
        if (isConvertToSub) {
          // Old clients wipe tagIds on to-sub; ownTagIds is non-empty here,
          // so the fleets always differ — re-assert unconditionally.
          return of(
            TaskSharedActions.updateTask({
              task: { id: taskId, changes: { tagIds: ownTagIds } },
            }),
          );
        }
        // To-main: an old client's reducer overwrites tagIds with its parent's
        // tags. Emit only when the kept tags differ — otherwise both fleets
        // already agree.
        const parent = state.entities[action.task.parentId as string];
        const oldClientTagIds = filterOutTodayTag(
          Array.isArray(parent?.tagIds) ? parent.tagIds : (action.parentTagIds ?? []),
        );
        if (fastArrayCompare(ownTagIds, oldClientTagIds)) {
          return EMPTY;
        }
        return of(
          TaskSharedActions.updateTask({
            task: { id: taskId, changes: { tagIds: ownTagIds } },
          }),
        );
      }),
    ),
  );

  /**
   * Starting a done task re-opens it. The `setCurrentTask` reducer only moves
   * the unsynced `currentTaskId` pointer (it may resolve a parent to one of its
   * subtasks), so the completion flip has to be its own persistent op or other
   * devices never learn about it (#9904). Reads `currentTaskId` after the
   * reducer ran so the op targets the task that was actually started.
   */
  reopenStartedDoneTask$ = createEffect(() =>
    this._actions$.pipe(
      ofType(setCurrentTask),
      withLatestFrom(this._store$.pipe(select(selectTaskFeatureState))),
      mergeMap(([, state]) => {
        const currentTaskId = state.currentTaskId;
        const currentTask = currentTaskId
          ? (state.entities[currentTaskId] as Task | undefined)
          : undefined;
        if (!currentTask || !currentTask.isDone) {
          return EMPTY;
        }
        return of(
          TaskSharedActions.updateTask({
            task: { id: currentTask.id, changes: { isDone: false } },
          }),
        );
      }),
    ),
  );

  planStartedTaskForToday$ = createEffect(() =>
    this._actions$.pipe(
      ofType(setCurrentTask),
      withLatestFrom(
        this._store$.pipe(select(selectTaskFeatureState)),
        this._store$.pipe(select(selectTodayTaskIds)),
        this._store$.pipe(select(selectTasksConfig)),
      ),
      mergeMap(([, state, todayTaskIds, tasksCfg]) => {
        const currentTaskId = state.currentTaskId;
        if (!currentTaskId) {
          return EMPTY;
        }

        const currentTask = state.entities[currentTaskId] as Task | undefined;
        if (
          !tasksCfg.isAutoAddWorkedOnToToday ||
          !currentTask ||
          !!currentTask.dueDay ||
          typeof currentTask.dueWithTime === 'number' ||
          todayTaskIds.includes(currentTaskId) ||
          (!!currentTask.parentId && todayTaskIds.includes(currentTask.parentId))
        ) {
          return EMPTY;
        }

        return of(
          TaskSharedActions.planTasksForToday({
            taskIds: [currentTaskId],
            today: this._dateService.todayStr(),
            startOfNextDayDiffMs: this._dateService.getStartOfNextDayDiffMs(),
            parentTaskMap: { [currentTaskId]: currentTask.parentId },
          }),
        );
      }),
    ),
  );

  autoSetNextTask$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        toggleStart,
        TaskSharedActions.updateTask,
        TaskSharedActions.deleteTask,
        TaskSharedActions.moveToArchive,

        moveProjectTaskToBacklogList.type,
        moveProjectTaskToBacklogListAuto.type,
      ),
      withLatestFrom(
        this._store$.pipe(select(selectConfigFeatureState)),
        this._store$.pipe(select(selectTaskFeatureState)),
        this._workContextSession.mainListTaskIds$,
        (action, globalCfg, state, todaysTaskIds) => ({
          action,
          state,
          isAutoStartNextTask: globalCfg.timeTracking.isAutoStartNextTask,
          todaysTaskIds,
        }),
      ),
      mergeMap(({ action, state, isAutoStartNextTask, todaysTaskIds }) => {
        const currentId = state.currentTaskId;
        let nextId: 'NO_UPDATE' | string | null;

        switch (action.type) {
          case toggleStart.type: {
            nextId = state.currentTaskId
              ? null
              : this._findNextTask(state, todaysTaskIds);
            break;
          }

          case TaskSharedActions.updateTask.type: {
            // TODO fix typing here
            const a = action as any;
            const { isDone } = a.task.changes;
            const oldId = a.task.id;
            const isCurrent = oldId === currentId;
            nextId =
              isDone && isCurrent
                ? isAutoStartNextTask
                  ? this._findNextTask(state, todaysTaskIds, oldId as string)
                  : null
                : 'NO_UPDATE';
            break;
          }

          case moveProjectTaskToBacklogList.type:
          case moveProjectTaskToBacklogListAuto.type: {
            const isCurrent = currentId === (action as any).taskId;
            nextId = isCurrent ? null : 'NO_UPDATE';
            break;
          }

          // QUICK FIX FOR THE ISSUE
          // TODO better solution
          case TaskSharedActions.deleteTask.type: {
            nextId = state.currentTaskId;
            break;
          }
          default:
            nextId = null;

          // NOTE: currently no solution for this, but we're probably fine, as the current task
          // gets unset every time we go to the finish day view
          // case TaskSharedActions.moveToArchive: {}
        }

        if (nextId === 'NO_UPDATE') {
          return EMPTY;
        } else {
          if (nextId) {
            return of(setCurrentTask({ id: nextId }));
          } else {
            return of(unsetCurrentTask());
          }
        }
      }),
    ),
  );

  private _findNextTask(
    state: TaskState,
    todaysTaskIds: string[],
    oldCurrentId?: string,
  ): string | null {
    let nextId: string | null = null;
    const { entities } = state;

    const filterUndoneNotCurrent = (id: string): boolean =>
      !(entities[id] as Task).isDone && id !== oldCurrentId;
    const flattenToSelectable = (arr: string[]): string[] =>
      arr.reduce((acc: string[], next: string) => {
        return (entities[next] as Task).subTaskIds.length > 0
          ? acc.concat((entities[next] as Task).subTaskIds)
          : acc.concat(next);
      }, []);

    if (oldCurrentId) {
      const oldCurTask = entities[oldCurrentId];
      if (oldCurTask && oldCurTask.parentId) {
        (entities[oldCurTask.parentId] as Task).subTaskIds.some((id) => {
          return id !== oldCurrentId && !(entities[id] as Task).isDone
            ? (nextId = id) && true // assign !!!
            : false;
        });
      }

      if (!nextId) {
        const oldCurIndex = todaysTaskIds.indexOf(oldCurrentId);
        const mainTasksBefore = todaysTaskIds.slice(0, oldCurIndex);
        const mainTasksAfter = todaysTaskIds.slice(oldCurIndex + 1);
        const selectableBefore = flattenToSelectable(mainTasksBefore);
        const selectableAfter = flattenToSelectable(mainTasksAfter);
        nextId =
          selectableAfter.find(filterUndoneNotCurrent) ||
          selectableBefore.reverse().find(filterUndoneNotCurrent) ||
          null;
        nextId = Array.isArray(nextId) ? nextId[0] : nextId;
      }
    } else {
      const lastTask = state.lastCurrentTaskId && entities[state.lastCurrentTaskId];
      const isLastSelectable =
        state.lastCurrentTaskId &&
        lastTask &&
        !lastTask.isDone &&
        !lastTask.subTaskIds.length;
      if (isLastSelectable) {
        nextId = state.lastCurrentTaskId;
      } else {
        const selectable =
          flattenToSelectable(todaysTaskIds).find(filterUndoneNotCurrent);
        nextId = Array.isArray(selectable) ? selectable[0] : selectable;
      }
    }

    return nextId;
  }
}
